import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Calendar, Download, Edit2, FileText, FilePlus2, Folder, Loader2, Plus, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { etapasContractualesApi } from '../../../lib/api';
import { cn, formatDate } from '../../../lib/utils';

const TIPO_OPTIONS = [
  { value: 'original', label: 'Contrato original' },
  { value: 'adenda', label: 'Adenda' },
  { value: 'renovacion', label: 'Renovación' },
  { value: 'modificacion', label: 'Modificación' },
  { value: 'prorroga', label: 'Prórroga' },
  { value: 'otro', label: 'Otro' },
];

const TIPO_BADGE_COLOR = {
  original: 'bg-blue-50 text-blue-700 ring-blue-200',
  adenda: 'bg-violet-50 text-violet-700 ring-violet-200',
  renovacion: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  modificacion: 'bg-amber-50 text-amber-700 ring-amber-200',
  prorroga: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  otro: 'bg-slate-50 text-slate-700 ring-slate-200',
};

const formatBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const initialForm = {
  tipo: 'original',
  titulo: '',
  fecha_firma: '',
  vigencia_desde: '',
  vigencia_hasta: '',
  notas: '',
};

export const DocumentosSection = forwardRef(({ contratoId, readOnly = false }, ref) => {
  const isPending = !contratoId;
  const [etapas, setEtapas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEtapa, setEditingEtapa] = useState(null); // null = creando, objeto = editando
  const [form, setForm] = useState(initialForm);
  const [pendingFiles, setPendingFiles] = useState([]); // archivos seleccionados (solo en creación)
  const [saving, setSaving] = useState(false);
  const [uploadingEtapaId, setUploadingEtapaId] = useState(null);
  const [deleteEtapa, setDeleteEtapa] = useState(null);

  // Helper: genera ids temporales locales
  const localId = (prefix) => `${prefix}_local_${Math.random().toString(36).slice(2, 10)}`;

  const fetchEtapas = async () => {
    if (isPending) return;
    setLoading(true);
    try {
      const res = await etapasContractualesApi.list(contratoId);
      setEtapas(res.data?.etapas || []);
    } catch (err) {
      toast.error('No se pudieron cargar las etapas del contrato');
    } finally {
      setLoading(false);
    }
  };

  // Exponer API imperativa para que el padre pueda volcar las etapas pendientes
  // cuando finalmente exista el contrato_id (caso Nuevo Contrato).
  useImperativeHandle(ref, () => ({
    hasPending: () => isPending && etapas.length > 0,
    flushTo: async (newContratoId) => {
      if (!newContratoId || !isPending) return { ok: true, etapas: 0, archivos: 0, errors: [] };
      let etapasOk = 0, archivosOk = 0, errors = [];
      for (const ep of etapas) {
        try {
          const payload = {
            tipo: ep.tipo,
            titulo: ep.titulo,
            fecha_firma: ep.fecha_firma || null,
            vigencia_desde: ep.vigencia_desde || null,
            vigencia_hasta: ep.vigencia_hasta || null,
            notas: ep.notas || null,
          };
          const res = await etapasContractualesApi.create(newContratoId, payload);
          const nueva = res.data;
          etapasOk++;
          for (const ar of (ep.archivos || [])) {
            if (!ar._file) continue;
            try {
              await etapasContractualesApi.uploadArchivo(newContratoId, nueva.etapa_id, ar._file);
              archivosOk++;
            } catch (err) {
              errors.push(`Archivo "${ar.nombre_original}" falló: ${err?.response?.data?.detail || err?.message || 'error'}`);
            }
          }
        } catch (err) {
          errors.push(`Etapa "${ep.titulo}" falló: ${err?.response?.data?.detail || err?.message || 'error'}`);
        }
      }
      return { ok: errors.length === 0, etapas: etapasOk, archivos: archivosOk, errors };
    },
  }), [isPending, etapas, contratoId]);

  useEffect(() => { fetchEtapas(); }, [contratoId]);

  const resetDialog = () => {
    setEditingEtapa(null);
    setForm(initialForm);
    setPendingFiles([]);
  };

  const openCreate = () => {
    resetDialog();
    setDialogOpen(true);
  };

  const openEdit = (etapa) => {
    setEditingEtapa(etapa);
    setForm({
      tipo: etapa.tipo || 'otro',
      titulo: etapa.titulo || '',
      fecha_firma: etapa.fecha_firma || '',
      vigencia_desde: etapa.vigencia_desde || '',
      vigencia_hasta: etapa.vigencia_hasta || '',
      notas: etapa.notas || '',
    });
    setPendingFiles([]);
    setDialogOpen(true);
  };

  const handleSaveEtapa = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!form.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }
    setSaving(true);
    try {
      if (isPending) {
        // Caso A/B: contrato todavía no existe — guardar localmente
        const nowIso = new Date().toISOString();
        if (editingEtapa) {
          setEtapas(prev => prev.map(ep => ep.etapa_id === editingEtapa.etapa_id
            ? { ...ep, ...form, updated_at: nowIso }
            : ep));
          toast.success('Etapa actualizada (se subirá al crear el contrato)');
        } else {
          const nueva = {
            etapa_id: localId('etp'),
            tipo: form.tipo,
            titulo: form.titulo.trim(),
            fecha_firma: form.fecha_firma || null,
            vigencia_desde: form.vigencia_desde || null,
            vigencia_hasta: form.vigencia_hasta || null,
            notas: form.notas || null,
            archivos: pendingFiles.map(file => ({
              archivo_id: localId('doc'),
              nombre_original: file.name,
              filename_storage: file.name,
              content_type: file.type,
              size: file.size,
              uploaded_at: nowIso,
              _file: file,
              _pending: true,
            })),
            created_at: nowIso,
            updated_at: nowIso,
            _pending: true,
          };
          setEtapas(prev => [...prev, nueva]);
          toast.success(`Etapa preparada${pendingFiles.length > 0 ? ` con ${pendingFiles.length} archivo(s)` : ''}. Se subirá al crear el contrato.`);
        }
      } else if (editingEtapa) {
        await etapasContractualesApi.update(contratoId, editingEtapa.etapa_id, form);
        toast.success('Etapa actualizada');
        await fetchEtapas();
      } else {
        const res = await etapasContractualesApi.create(contratoId, form);
        const nueva = res.data;
        if (pendingFiles.length > 0) {
          for (const file of pendingFiles) {
            try {
              await etapasContractualesApi.uploadArchivo(contratoId, nueva.etapa_id, file);
            } catch (err) {
              toast.error(`Falló la subida de ${file.name}`);
            }
          }
        }
        toast.success(`Etapa creada${pendingFiles.length > 0 ? ` con ${pendingFiles.length} archivo(s)` : ''}`);
        await fetchEtapas();
      }
      setDialogOpen(false);
      resetDialog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar la etapa');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteEtapa = async () => {
    if (!deleteEtapa) return;
    try {
      if (isPending) {
        setEtapas(prev => prev.filter(ep => ep.etapa_id !== deleteEtapa.etapa_id));
        toast.success('Etapa eliminada');
      } else {
        await etapasContractualesApi.delete(contratoId, deleteEtapa.etapa_id);
        toast.success('Etapa eliminada');
        await fetchEtapas();
      }
      setDeleteEtapa(null);
    } catch (err) {
      toast.error('No se pudo eliminar la etapa');
    }
  };

  const handleUploadToEtapa = async (etapa, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (isPending) {
      // Agregar a memoria local
      const nowIso = new Date().toISOString();
      setEtapas(prev => prev.map(ep => ep.etapa_id === etapa.etapa_id
        ? {
            ...ep,
            archivos: [
              ...(ep.archivos || []),
              ...files.map(file => ({
                archivo_id: localId('doc'),
                nombre_original: file.name,
                filename_storage: file.name,
                content_type: file.type,
                size: file.size,
                uploaded_at: nowIso,
                _file: file,
                _pending: true,
              })),
            ],
            updated_at: nowIso,
          }
        : ep));
      e.target.value = '';
      toast.success(`${files.length} archivo(s) agregado(s) — se subirán al crear el contrato`);
      return;
    }
    setUploadingEtapaId(etapa.etapa_id);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        await etapasContractualesApi.uploadArchivo(contratoId, etapa.etapa_id, file);
        ok++;
      } catch (err) {
        fail++;
      }
    }
    setUploadingEtapaId(null);
    e.target.value = '';
    if (ok > 0) toast.success(`${ok} archivo(s) subido(s)`);
    if (fail > 0) toast.error(`${fail} archivo(s) fallaron`);
    await fetchEtapas();
  };

  const handleDeleteArchivo = async (etapa, archivo) => {
    if (!window.confirm(`¿Eliminar archivo "${archivo.nombre_original}"?`)) return;
    try {
      if (isPending) {
        setEtapas(prev => prev.map(ep => ep.etapa_id === etapa.etapa_id
          ? { ...ep, archivos: (ep.archivos || []).filter(a => a.archivo_id !== archivo.archivo_id) }
          : ep));
        toast.success('Archivo eliminado');
      } else {
        await etapasContractualesApi.deleteArchivo(contratoId, etapa.etapa_id, archivo.archivo_id);
        toast.success('Archivo eliminado');
        await fetchEtapas();
      }
    } catch (err) {
      toast.error('No se pudo eliminar el archivo');
    }
  };

  const handleDownloadArchivo = (etapa, archivo) => {
    // Caso local (pending): el File está en memoria
    if (archivo._file) {
      const blobUrl = URL.createObjectURL(archivo._file);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = archivo.nombre_original;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      return;
    }
    const url = etapasContractualesApi.downloadUrl(contratoId, etapa.etapa_id, archivo.archivo_id);
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('No autorizado');
        return r.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = archivo.nombre_original;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('No se pudo descargar el archivo'));
  };

  return (
    <div className="space-y-3" data-testid="documentos-section">
      {isPending && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-2" data-testid="documentos-pending-banner">
          <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Podés cargar documentos ahora o crear el contrato sin archivos y agregarlos después. Las etapas y archivos preparados acá se suben automáticamente al presionar <strong>&quot;Crear Contrato&quot;</strong>.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Cada etapa contractual agrupa archivos de un mismo evento (firma original, adenda, renovación, etc.).
        </div>
        {!readOnly && (
          <Button type="button" size="sm" onClick={openCreate} data-testid="btn-nueva-carga-documentos">
            <Plus className="w-4 h-4 mr-1" /> Nueva carga de documentos
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
          Cargando etapas...
        </div>
      ) : etapas.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center" data-testid="documentos-empty">
          <Folder className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 font-medium">Sin etapas cargadas todavía</p>
          <p className="text-xs text-slate-400 mt-1">Empezá con &quot;+ Nueva carga de documentos&quot; para crear la primera etapa.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {etapas.map((etapa) => (
            <div key={etapa.etapa_id} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`etapa-${etapa.etapa_id}`}>
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium uppercase ring-1', TIPO_BADGE_COLOR[etapa.tipo] || TIPO_BADGE_COLOR.otro)}>
                      {TIPO_OPTIONS.find(t => t.value === etapa.tipo)?.label || etapa.tipo}
                    </span>
                    <h4 className="text-sm font-semibold text-slate-900 truncate">{etapa.titulo}</h4>
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {etapa.fecha_firma && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Firma: {formatDate(etapa.fecha_firma)}
                      </span>
                    )}
                    {(etapa.vigencia_desde || etapa.vigencia_hasta) && (
                      <span>Vigencia: {etapa.vigencia_desde ? formatDate(etapa.vigencia_desde) : '?'} → {etapa.vigencia_hasta ? formatDate(etapa.vigencia_hasta) : '?'}</span>
                    )}
                    <span>{etapa.archivos?.length || 0} archivo(s)</span>
                  </div>
                  {etapa.notas && (
                    <p className="text-xs text-slate-600 mt-1 italic">{etapa.notas}</p>
                  )}
                </div>
                {!readOnly && (
                  <div className="flex items-center gap-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(etapa)} data-testid={`btn-edit-etapa-${etapa.etapa_id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setDeleteEtapa(etapa)} data-testid={`btn-delete-etapa-${etapa.etapa_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="px-4 py-3">
                {(etapa.archivos && etapa.archivos.length > 0) ? (
                  <ul className="space-y-1 mb-2">
                    {etapa.archivos.map((a) => (
                      <li key={a.archivo_id} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-slate-50" data-testid={`archivo-${a.archivo_id}`}>
                        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="flex-1 truncate text-slate-700">{a.nombre_original}</span>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{formatBytes(a.size)}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleDownloadArchivo(etapa, a)} data-testid={`btn-download-${a.archivo_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {!readOnly && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => handleDeleteArchivo(etapa, a)} data-testid={`btn-delete-archivo-${a.archivo_id}`}>
                            <X className="w-3.5 h-3.5 text-rose-600" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-400 italic mb-2">Sin archivos cargados</p>
                )}
                {!readOnly && (
                  <label className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-800 cursor-pointer" data-testid={`btn-add-archivos-${etapa.etapa_id}`}>
                    {uploadingEtapaId === etapa.etapa_id ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo...
                      </>
                    ) : (
                      <>
                        <FilePlus2 className="w-3.5 h-3.5" /> Agregar archivos a esta etapa
                      </>
                    )}
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                      disabled={uploadingEtapaId === etapa.etapa_id}
                      onChange={(e) => handleUploadToEtapa(etapa, e)}
                    />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog: crear / editar etapa */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetDialog(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-etapa">
          <DialogHeader>
            <DialogTitle>{editingEtapa ? 'Editar etapa contractual' : 'Nueva carga de documentos'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); handleSaveEtapa(e); } }}>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm(prev => ({ ...prev, tipo: v }))}>
                <SelectTrigger data-testid="select-tipo-etapa"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Título *</Label>
              <Input
                value={form.titulo}
                onChange={(e) => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ej: Contrato original, Adenda 1, Renovación 2026..."
                required
                data-testid="input-titulo-etapa"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Fecha de firma</Label>
                <Input type="date" value={form.fecha_firma} onChange={(e) => setForm(prev => ({ ...prev, fecha_firma: e.target.value }))} data-testid="input-fecha-firma" />
              </div>
              <div>
                <Label className="text-xs">Vigencia desde</Label>
                <Input type="date" value={form.vigencia_desde} onChange={(e) => setForm(prev => ({ ...prev, vigencia_desde: e.target.value }))} data-testid="input-vigencia-desde" />
              </div>
              <div>
                <Label className="text-xs">Vigencia hasta</Label>
                <Input type="date" value={form.vigencia_hasta} onChange={(e) => setForm(prev => ({ ...prev, vigencia_hasta: e.target.value }))} data-testid="input-vigencia-hasta" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Textarea
                value={form.notas}
                onChange={(e) => setForm(prev => ({ ...prev, notas: e.target.value }))}
                rows={2}
                placeholder="Observaciones de esta etapa"
                data-testid="input-notas-etapa"
              />
            </div>
            {!editingEtapa && (
              <div>
                <Label className="text-xs">Archivos (opcional, podés agregar después)</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
                  className="block w-full text-sm text-slate-700 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  data-testid="input-archivos-etapa"
                />
                {pendingFiles.length > 0 && (
                  <p className="text-xs text-slate-600 mt-1">{pendingFiles.length} archivo(s) seleccionado(s): {pendingFiles.map(f => f.name).join(', ')}</p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetDialog(); }}>Cancelar</Button>
              <Button type="button" disabled={saving} onClick={handleSaveEtapa} data-testid="btn-guardar-etapa">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {editingEtapa ? 'Guardar cambios' : 'Crear etapa'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado de etapa */}
      <Dialog open={!!deleteEtapa} onOpenChange={(o) => { if (!o) setDeleteEtapa(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-delete-etapa">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Eliminar etapa contractual</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            ¿Eliminar la etapa <strong>{deleteEtapa?.titulo}</strong>
            {(deleteEtapa?.archivos?.length || 0) > 0 && (
              <> y sus <strong>{deleteEtapa.archivos.length}</strong> archivo(s)</>
            )}? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteEtapa(null)}>Cancelar</Button>
            <Button type="button" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleConfirmDeleteEtapa} data-testid="btn-confirm-delete-etapa">
              Eliminar etapa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
DocumentosSection.displayName = 'DocumentosSection';
